import { Injectable } from "@angular/core";
import { HttpHeaders, HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { Sale } from "../models/sale";
import { environment } from "src/environments/environment";
import { catchError, map } from "rxjs/operators";
import { UfSale } from "../models/ufs-sale";
import { CitySale } from "../models/city-sale";
import { NeighborhoodSale } from "../models/neighborhood-sale";
import { ParamsVendas } from "src/app/settings/models/params-vendas";
import { SaleRecordsResponse } from "../models/sale-record";

@Injectable({
  providedIn: "root",
})
export class SalesService {
  private httpOptions = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
  };

  constructor(private http: HttpClient) {}

  // teams = _id das equipes, sellers = codigo_externo dos vendedores. Vazios = "Todos".
  getSales(
    cnpj: string,
    fromDate: Date,
    toDate: Date,
    supervisor: string,
    teams: string[] = [],
    sellers: string[] = []
  ): Observable<Sale[]> {
    if (cnpj === "") {
      return of([]);
    }

    const fromDateTz = fromDate.toISOString().substr(0, 10).concat("T00:00:00-03:00");
    const toDateTz = toDate.toISOString().substr(0, 10).concat("T23:59:59-03:00");

    fromDate = new Date(fromDateTz);
    toDate = new Date(toDateTz);

    const match: any = {
      cnpj,
      data_da_criacao_app: {
        $gte: fromDate,
        $lte: toDate.setHours(23, 59, 59, 999),
      },
    };

    if (supervisor) {
      match.$or = [{ "equipe.supervisor.id": supervisor }, { "equipe.gestor.id": supervisor }];
    }

    if (teams.length) {
      match["equipe.id"] = { $in: teams };
    }

    if (sellers.length) {
      match["vendedor.codigo_externo"] = { $in: sellers };
    }

    const aggregations = { query: [{ $match: match }] };

    return this.http
      .post<Sale[]>(`${environment.vendas}/vendautil`, aggregations, this.httpOptions)
      .pipe(catchError(err => of(err.error)));
  }

  getSale(cnpj: string, id: string): Observable<Sale> {
    if (cnpj === "" || id === "") {
      return of();
    }

    return this.http.get<Sale>(`${environment.vendas}/vendautil/${cnpj}/${id}`).pipe(catchError(err => of(err.error)));
  }

  updateSale(sale: Sale, status: string, motivo?: string): Observable<Sale> {
    const { _id, cnpj } = sale;
    if (cnpj === "" || _id === "" || status === "") {
      return of();
    }

    console.log(sale);

    sale.status = status;
    sale.plano = sale.plano && sale.plano.codigo ? sale.plano.codigo : sale.plano;
    sale.titular.telefone = sale.titular.telefone ? sale.titular.telefone.replace(/\D/g, "") : "";
    sale.titular.celular = sale.titular.celular ? sale.titular.celular.replace(/\D/g, "") : "";

    if (status === "devolvido") {
      sale.motivo_devolvido = motivo;
    } else if (status === "deletado") {
      sale.motivo_deletado = motivo;
    }

    return this.http
      .put<Sale>(`${environment.vendas}/web/vendautil/${cnpj}/${_id}`, sale, this.httpOptions)
      .pipe(catchError(err => of(err.error)));
  }

  getUFs(cnpj: string): Observable<UfSale[]> {
    if (cnpj === "") {
      return of([]);
    }

    return this.http
      .get<UfSale[]>(`${environment.vendas}/neighborhood/show?cnpj=${cnpj}&type=UF`, this.httpOptions)
      .pipe(catchError(err => of(err.error)));
  }

  getCities(cnpj: string, uf: string): Observable<CitySale[]> {
    if (cnpj === "" || uf === "") {
      return of([]);
    }

    return this.http
      .get<CitySale[]>(`${environment.vendas}/neighborhood/show?cnpj=${cnpj}&uf=${uf}&type=CITY`, this.httpOptions)
      .pipe(catchError(err => of(err.error)));
  }

  getNeighborhood(cnpj: string, city: string): Observable<NeighborhoodSale[]> {
    if (cnpj === "" || city === "") {
      return of([]);
    }

    return this.http
      .get<NeighborhoodSale[]>(
        `${environment.vendas}/neighborhood/show?cnpj=${cnpj}&city=${city}&type=DESCRIPTION`,
        this.httpOptions
      )
      .pipe(catchError(err => of(err.error)));
  }

  getParams(cnpj: string) {
    if (cnpj === "") {
      return of();
    }

    return this.http.get<ParamsVendas>(environment.vendas + "/params/" + cnpj);
  }

  // Registro completo de assinatura de uma venda. Usado no detalhe da venda,
  // que precisa de meta.providerResult (URL de assinatura, destinatarios).
  getSaleRecord(saleId: string) {
    if (saleId === "") {
      return of();
    }

    return this.http
      .get<any>(`${environment.backend_virtus}/signature/sale/${saleId}/record`, this.httpOptions)
      .pipe(catchError(err => of(err.error)));
  }

  // Apenas saleId + status, para a listagem. Uma chamada por venda derruba a
  // performance da tela de Gestao de Vendas (DTGD-1443).
  getSaleRecords(saleIds: string[]): Observable<SaleRecordsResponse> {
    if (!saleIds.length) {
      return of({ records: [] });
    }

    return this.http
      .post<SaleRecordsResponse>(
        `${environment.backend_virtus}/signature/sales/records`,
        { saleIds },
        this.httpOptions
      )
      .pipe(catchError(() => of({ records: [] })));
  }
}
